import { apiEndpoint } from "../config/config";
import { safeReturnTo } from "./returnTo";

/**
 * Talking to our own auth routes — `patreon-direct-auth` task 9.
 *
 * Replaces `@auth0/auth0-react`. The shape of what the app consumes is
 * unchanged (`useSession` still returns the same fields), but almost nothing
 * about the mechanism is:
 *
 * - **The token is not here, and cannot be.** It is an `httpOnly` cookie, so
 *   no code on this page can read it, including this file. That is the point
 *   of PD-D1 and it is strictly better than the `localStorage` the Auth0
 *   provider was configured to use, where any injected script could take it.
 * - **Signing in is a navigation, not a call.** OAuth needs a top-level
 *   redirect; there is nothing to await.
 * - **`returnTo` is not our problem after this line.** It is signed into the
 *   state cookie and the server redirects to it, re-validating on the way out.
 *   No `appState`, no `onRedirectCallback`, nothing in the address bar to
 *   clean up afterwards.
 */

/**
 * What the server says about this browser.
 *
 * Three states, not two. "Not yet known" is not "signed out" — conflating them
 * is the bug that bounced the owner to `/tabs` on 2026-08-31, because every
 * screen that mounted during the restore decided the viewer was a stranger and
 * never re-decided.
 */
export type Session =
  | { status: "loading" }
  | { status: "signed-out" }
  | { status: "signed-in"; sub: string; expiresAt: number };

interface SessionResponse {
  authenticated: boolean;
  sub?: string;
  expiresAt?: number;
}

/**
 * `GET /auth/session`.
 *
 * Answers 200 either way, so a non-OK response here means something is
 * genuinely wrong rather than that nobody is signed in. Both are treated as
 * signed out, because there is no third thing the app can do about it, but
 * only one of them is worth a line in the console.
 */
export async function fetchSession(): Promise<Session> {
  const response = await fetch(`${apiEndpoint}/auth/session`, {
    // Without this the cookie is not sent and every visitor is a stranger.
    credentials: "include",
  });
  if (!response.ok) {
    console.error(`[auth] /auth/session answered ${response.status}`);
    return { status: "signed-out" };
  }
  const body = (await response.json()) as SessionResponse;
  if (!body.authenticated || !body.sub) return { status: "signed-out" };
  return { status: "signed-in", sub: body.sub, expiresAt: body.expiresAt ?? 0 };
}

/**
 * Where to send someone to sign in.
 *
 * `returnTo` is validated here as well as twice on the server. Sending a value
 * we know would be refused is pointless, and it is the call sites — not this
 * module — that read a path out of navigation state, so this is where an
 * unchecked one would enter.
 */
export function signInUrl(returnTo?: string): string {
  const safe = returnTo === undefined ? null : safeReturnTo(returnTo);
  const query = safe ? `?returnTo=${encodeURIComponent(safe)}` : "";
  return `${apiEndpoint}/auth/start${query}`;
}

/**
 * `POST /auth/logout`, then home.
 *
 * The reload is not cosmetic: the cookie is gone but every component still
 * holds state derived from a session that no longer exists, and a full
 * navigation is the only way to be sure none of it survives. It happens even
 * if the request fails — a person who pressed sign out should end up signed
 * out of the page in front of them regardless of what the network did.
 */
export async function requestSignOut(): Promise<void> {
  try {
    await fetch(`${apiEndpoint}/auth/logout`, {
      method: "POST",
      credentials: "include",
    });
  } catch (error) {
    console.error("[auth] sign-out request failed; clearing locally anyway", error);
  }
  window.location.assign(window.location.origin);
}

/**
 * Delete the tokens the Auth0 provider left in `localStorage`.
 *
 * `main.tsx` used to set `cacheLocation="localstorage"`, so every visitor who
 * signed in before task 9 has an access token *and a rotating refresh token*
 * sitting under `@@auth0spajs@@` keys in their browser. Removing the code that
 * writes them does not remove the ones already written.
 *
 * Leaving them would be a strange place to stop: the entire argument for the
 * `httpOnly` cookie is that a token in `localStorage` is readable by any script
 * injected into the page, and those tokens stay valid until they expire whether
 * or not this app ever reads them again.
 *
 * Prefix-matched rather than named exactly, because the key encodes the client
 * id and audience, and both changed across environments. Wrapped because
 * `localStorage` throws outright in some privacy modes — failing to tidy up is
 * not a reason to fail to start.
 */
export function clearLegacyAuth0Storage(): void {
  try {
    const stale = Object.keys(window.localStorage).filter((key) =>
      key.startsWith("@@auth0spajs@@")
    );
    for (const key of stale) window.localStorage.removeItem(key);
  } catch {
    // Nothing to do and nothing worth saying: the storage we cannot read is
    // also storage the old provider could not have written to.
  }
}
